import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import {useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import styled from 'styled-components';

import {globalDocument} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {buildDashedBorderBackground} from '../../../libs/shared/ui/src/styles/mixins';
import {ThemeContextType, useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import avatarDefaultImage from '../../assets/images/Avatars/Teammate/80x80/Placeholder - Filled.png';
import {ImageTeammateAvatar} from '../../components/common/avatars/imageTeammateAvatar';
import {Dialog} from '../../components/common/dialogs/dialog';
import {DialogHeader} from '../../components/common/dialogs/dialogHeader';
import {CircleSpinner} from '../../components/common/spinner/circleSpinner';
import {InlineBanner, InlineBannerTypesEnum} from '../../components/inlineBanner/inlineBanner';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {updateUserAvatar} from '../../store/data/teammates/teammatesActions';

/*
 * Props.
 */

interface SettingsUserAvatarDialogProps {
  onRequestClose: () => void;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  width: 420px;
  height: 314px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const StyledFooterDiv = styled.div`
  display: flex;
  padding: 20px;
`;

const StyledLabelDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.normal};
`;

const StyledButtonGroupDiv = styled.div`
  display: flex;
  gap: 10px;
`;

interface StyleProps {
  $isOver: boolean;
  $hasError: boolean;
}

const StyledDropzone = styled.div<StyleProps>`
  width: 380px;
  height: 182px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  background-color: ${(p) => (p.$isOver ? p.theme.palette.blue.shade10 : 'transparent')};
  ${(p) =>
    buildDashedBorderBackground({
      dashArray: '6,6',
      strokeColor: computeBorderColor(p),
      strokeWidth: 2,
      borderRadius: 16,
      strokeLinejoin: 'round',
    })}
`;

function computeBorderColor(p: StyleProps & ThemeContextType) {
  if (p.$isOver) {
    return p.theme.palette.blue.shade40;
  }

  if (p.$hasError) {
    return p.theme.palette.red.shade40;
  }

  return p.theme.greys.shade40;
}

const StyledDropzoneLabel = styled.span`
  margin-top: 16px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  margin-bottom: 4px;
`;

const StyledInlineBanner = styled(InlineBanner)`
  padding-top: 7px;
  padding-bottom: 7px;
`;

const StyledErrorsWrapperDiv = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledAvatarWrapperDiv = styled.div`
  position: relative;
`;

const StyledCircleSpinner = styled(CircleSpinner)`
  position: absolute;
  pointer-events: none;
  z-index: 1;
  top: -2px;
  left: -2px;
`;

const StyledProfileOverlayAvatarDiv = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${(p) => p.theme.alphas.black50};
  color: ${(p) => p.theme.greys.white};
  text-align: center;
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.medium};
  z-index: 1;
  opacity: 0;
  line-height: 80px;
  border-radius: 100%;

  ${StyledAvatarWrapperDiv}:hover & {
    opacity: 1;
  }
`;

/*
 * Component.
 */

interface FileDragItem {
  files?: ReadonlyArray<File>;
}

enum AvatarErrorsEnum {
  TOO_BIG = 'TOO_BIG',
  WRONG_TYPE = 'WRONG_TYPE',
}

const acceptableAvatarTypes = ['png', 'jpeg', 'jpg'];

export const SettingsUserAvatarDialog: FC<SettingsUserAvatarDialogProps> = (props) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const [errors, setErrors] = useState<AvatarErrorsEnum[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showProgressSpinner, setShowProgressSpinner] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [avatar, setAvatar] = useState<string | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);

  const onChooseFileClick = () => {
    const input = globalDocument.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.accept = acceptableAvatarTypes.map((i) => `image/${i}`).join(',');
    input.onchange = () => {
      handleDrop({files: Array.from(input.files || [])});
    };
    input.click();
  };

  const handleDrop = (item: FileDragItem) => {
    setErrors([]);
    setIsDirty(true);
    const {files} = item;
    const [firstFile] = Array.from(files || []);
    const tooBig = firstFile.size > 1024 * 1024 && AvatarErrorsEnum.TOO_BIG;
    const wrongType =
      acceptableAvatarTypes.every((ext) => firstFile.type !== `image/${ext}`) && AvatarErrorsEnum.WRONG_TYPE;
    const newErrors = _([tooBig, wrongType]).compact().uniq().value();

    if (newErrors.length > 0) {
      setErrors(newErrors);
    } else {
      if (avatar) {
        URL.revokeObjectURL(avatar);
      }
      setFile(firstFile);
      setAvatar(URL.createObjectURL(firstFile));
    }
  };

  const renderErrors = () =>
    errors.map((e) => (
      <StyledInlineBanner
        key={e}
        type={InlineBannerTypesEnum.ERROR}
        size={VisualSizesEnum.SMALL}
        title={errorRenderers[e]}
        hasSmallBorderRadius
      />
    ));

  const [{isOver}, dropRef] = useDrop<FileDragItem, void, {isOver: boolean}>({
    accept: [NativeTypes.FILE],
    drop: (item) => handleDrop(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const onSave = () => {
    if (!file) {
      return;
    }

    const spinnerTimeout = setTimeout(() => {
      setShowProgressSpinner(true);
    }, 1000);
    dispatch(
      updateUserAvatar(file, (progress) => {
        setUploadProgress(progress);
      }),
    ).then(() => {
      setUploadProgress(null);
      clearTimeout(spinnerTimeout);
      props.onRequestClose();
    });
  };

  const onCancel = async () => {
    props.onRequestClose();
  };

  const canSave = useMemo(
    () => errors.length === 0 && isDirty && file && (avatar || '')?.length > 0 && !showProgressSpinner,
    [avatar, errors.length, file, isDirty, showProgressSpinner],
  );

  return (
    <Dialog onRequestClose={props.onRequestClose}>
      <DialogHeader title="Front profile picture" />
      <StyledContentDiv>
        <StyledDropzone ref={dropRef} $isOver={isOver} $hasError={errors.length > 0}>
          <StyledAvatarWrapperDiv onClick={!showProgressSpinner ? onChooseFileClick : undefined}>
            {showProgressSpinner && (
              <StyledCircleSpinner
                color={theme.palette.blue.shade40}
                strokeWidth={2}
                progress={uploadProgress ?? 0}
                size={82}
              />
            )}
            <StyledProfileOverlayAvatarDiv />
            <ImageTeammateAvatar avatarUrl={avatar ?? avatarDefaultImage} size={80} />
          </StyledAvatarWrapperDiv>
          <StyledDropzoneLabel>
            <Trans id="settingsprofile-dragavatar">Drag and drop your image or</Trans>
          </StyledDropzoneLabel>
          <Button
            size={VisualSizesEnum.MEDIUM}
            variant={ButtonVariantsEnum.LINK}
            onClick={onChooseFileClick}
            isDisabled={showProgressSpinner}
          >
            <Trans id="settingsprofile-choosefile">Choose file</Trans>
          </Button>
        </StyledDropzone>
        <StyledErrorsWrapperDiv>{renderErrors()}</StyledErrorsWrapperDiv>
      </StyledContentDiv>
      <StyledFooterDiv>
        <StyledLabelDiv>
          <Trans id="settingsprofile-avatar-copy">For Front collaboration, not external communication</Trans>
        </StyledLabelDiv>
        <StyledButtonGroupDiv>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancel}>
            <Trans id="settingsprofile-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={!canSave} onClick={onSave}>
            <Trans id="settingsprofile-save">Save</Trans>
          </Button>
        </StyledButtonGroupDiv>
      </StyledFooterDiv>
    </Dialog>
  );
};

const errorRenderers: {[k in AvatarErrorsEnum]: ReactNode} = {
  [AvatarErrorsEnum.TOO_BIG]: <Trans id="settingsprofile-imagetoobig">Image is too big (&gt;1MB)</Trans>,
  [AvatarErrorsEnum.WRONG_TYPE]: (
    <Trans id="settingsprofile-onlyjpgpng">Only JPEG and PNG files are supported</Trans>
  ),
};
